// app/javascript/controllers/verify_rules_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = []

  connect() {
    setTimeout(() => this.checkRules(), 500)

    this.element.addEventListener('change', () => {
      setTimeout(() => this.checkRules(), 300)
    })

    this.element.addEventListener('input', () => {
      setTimeout(() => this.checkRules(), 300)
    })

    const startAtInput = this.element.querySelector('input[name*="[start_at]"]')
    if (startAtInput) {
      startAtInput.addEventListener('change', () => {
        setTimeout(() => this.checkRules(), 300)
      })
    }

    this.element.addEventListener('click', (event) => {
      if (event.target.closest('button[data-action*="location-selector#saveLocation"]')) {
        setTimeout(() => this.checkRules(), 500)
      }
    })

    this.setupLocationObserver()

    this.element.addEventListener('tomselect:change', () => {
      setTimeout(() => this.checkRules(), 300)
    })

    document.addEventListener('turbo:frame-load', () => {
      this.setupLocationObserver()
      setTimeout(() => this.checkRules(), 500)
    })

    document.addEventListener('turbo:before-stream-render', () => {
      this.setupLocationObserver()
      setTimeout(() => this.checkRules(), 500)
    })
  }

  setupLocationObserver() {
    if (!this.locationObservers) {
      this.locationObservers = []
    }

    const locationTextElements = this.element.querySelectorAll('[data-location-selector-target="locationText"]')
    
    locationTextElements.forEach((element) => {
      if (element.dataset.observing === 'true') return
      element.dataset.observing = 'true'

      const observer = new MutationObserver(() => {
        setTimeout(() => this.checkRules(), 300)
      })
      
      observer.observe(element, {
        childList: true,
        characterData: true,
        subtree: true
      })

      this.locationObservers.push(observer)
    })

    const displayLocationElements = this.element.querySelectorAll('[data-location-selector-target="displayLocation"]')
    const selectorButtonElements = this.element.querySelectorAll('[data-location-selector-target="selectorButton"]')
    
    const allLocationElements = [...displayLocationElements, ...selectorButtonElements]
    
    allLocationElements.forEach((element) => {
      if (element.dataset.observing === 'true') return
      element.dataset.observing = 'true'

      const observer = new MutationObserver(() => {
        setTimeout(() => this.checkRules(), 300)
      })
      
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class']
      })

      this.locationObservers.push(observer)
    })
  }

  checkRules() {
    const experiences = this.getExperiences()
    const abRulesSelect = this.getAbRulesSelect()
    
    if (!abRulesSelect) return

    if (experiences.length < 2) {
      this.disableAbRulesSelect(abRulesSelect)
      this.hideOverlapSuggestion()
      this.showAllDayTimeCards()
      return
    }

    const hasOverlap = this.hasOverlappingExperiences(experiences)
    
    if (hasOverlap) {
      this.enableAbRulesSelect(abRulesSelect)
      const overlapPairs = this.getOverlapPairs(experiences)
      this.showOnlyOverlappingDayTimeCards(overlapPairs)
    } else {
      this.disableAbRulesSelect(abRulesSelect)
      this.hideOverlapSuggestion()
      this.showAllDayTimeCards()
    }
  }

  hasOverlappingExperiences(experiences) {
    for (let i = 0; i < experiences.length; i++) {
      for (let j = i + 1; j < experiences.length; j++) {
        if (this.experiencesOverlap(experiences[i], experiences[j])) return true
      }
    }
    return false
  }

  experiencesOverlap(expA, expB) {
    return this.locationOverlap(expA, expB) &&
      this.scheduleOverlap(expA, expB) &&
      this.deviceOverlap(expA, expB)
  }

  locationOverlap(expA, expB) {
    const codesA = expA.country_code || []
    const codesB = expB.country_code || []
    if (codesA.length === 0 || codesB.length === 0) return true
    return codesA.some(c => codesB.includes(c))
  }

  scheduleOverlap(expA, expB) {
    const startA = expA.start_at ? new Date(expA.start_at).getTime() : 0
    const endA = expA.end_at ? new Date(expA.end_at).getTime() : Infinity
    const startB = expB.start_at ? new Date(expB.start_at).getTime() : 0
    const endB = expB.end_at ? new Date(expB.end_at).getTime() : Infinity
    return startA <= endB && startB <= endA
  }

  deviceOverlap(expA, expB) {
    const devA = this.normalizeDevice(expA.devices)
    const devB = this.normalizeDevice(expB.devices)
    return devA === 'all' || devB === 'all' || devA === devB
  }

  normalizeDevice(devices) {
    if (!devices || devices === 'All devices') return 'all'
    if (typeof devices === 'string' && devices.toLowerCase() === 'all') return 'all'
    return devices
  }

  getOverlapPairs(experiences) {
    const pairs = []
    const overlappingIndices = new Set()
    for (let i = 0; i < experiences.length; i++) {
      for (let j = i + 1; j < experiences.length; j++) {
        if (this.experiencesOverlap(experiences[i], experiences[j])) {
          pairs.push({ indexA: i, indexB: j, nameA: experiences[i].name, nameB: experiences[j].name })
          overlappingIndices.add(i)
          overlappingIndices.add(j)
        }
      }
    }
    return { pairs, overlappingIndices }
  }

  showOverlapSuggestion(overlapData) {
    const el = document.getElementById('abRulesSuggestion')
    if (!el) return
    if (overlapData.pairs.length === 0) {
      el.classList.add('hidden')
      return
    }
    const text = overlapData.pairs.map(p => `${p.nameA} and ${p.nameB}`).join('; ')
    el.innerHTML = `A/B rules apply to: <strong>${text}</strong>`
    el.classList.remove('hidden')
  }

  hideOverlapSuggestion() {
    const el = document.getElementById('abRulesSuggestion')
    if (el) el.classList.add('hidden')
  }

  showOnlyOverlappingDayTimeCards(overlapData) {
    const indices = overlapData.overlappingIndices
    document.querySelectorAll('[data-experience-index]').forEach(card => {
      const idx = parseInt(card.dataset.experienceIndex, 10)
      card.style.display = indices.has(idx) ? '' : 'none'
    })
  }

  showAllDayTimeCards() {
    document.querySelectorAll('[data-experience-index]').forEach(card => {
      card.style.display = ''
    })
  }

  getAbRulesSelect() {
    const form = this.element.closest('form')
    if (!form) return null

    return form.querySelector('select[name*="[ab_rules]"]')
  }

  disableAbRulesSelect(select) {
    if (!select) return

    if (select.value !== 'automatic') {
      select.value = 'automatic'
      
      if (select?.tomselect) {
        select.tomselect.setValue('automatic')
      }

      select.dispatchEvent(new Event('change', { bubbles: true }))
    }

    select.disabled = true
    
    this.hideElements(select);

    const selectContainer = select.closest('.relative')
    if (selectContainer) {
      selectContainer.style.opacity = '0.5'
      selectContainer.style.pointerEvents = 'none'
    }
  }

  enableAbRulesSelect(select) {
    if (!select) return

    this.showElements(select);

    select.disabled = false
    
    if (window.TomSelect && select.tomselect) {
      select.tomselect.enable()
    }

    const selectContainer = select.closest('.relative')
    if (selectContainer) {
      selectContainer.style.opacity = '1'
      selectContainer.style.pointerEvents = 'auto'
    }
  }

  getExperiences() {
    const allRows = this.element.querySelectorAll('.flex.gap-4')
    const experiences = []

    allRows.forEach((row) => {
      if (row.querySelector('.text-gray-400')) {
        return
      }

      const hasExperienceFields = row.querySelector('input[name*="[start_at]"]') || 
                                   row.querySelector('input[name*="[end_at]"]') ||
                                   row.querySelector('select[name*="[devices]"]')
      
      if (!hasExperienceFields) {
        return
      }

      const experience = {
        name: this.getExperienceName(row),
        country_code: this.getCountryCodes(row),
        state: this.getStateCodes(row),
        start_at: this.getFieldValue(row, 'input[name*="[start_at]"]'),
        end_at: this.getFieldValue(row, 'input[name*="[end_at]"]'),
        devices: this.getDevicesRaw(row),
        schedule: this.getSchedule(row),
        location: this.getLocation(row)
      }

      experiences.push(experience)
    })

    return experiences
  }

  getExperienceName(row) {
    const nameDiv = row.querySelector('.truncate')
    return nameDiv?.textContent?.trim() || 'Experience'
  }

  getCountryCodes(row) {
    const select = row.querySelector('select[name*="[country_code]"]')
    if (!select) return []
    if (select.tomselect) {
      const val = select.tomselect.getValue()
      return Array.isArray(val) ? val : (val ? [val] : [])
    }
    return Array.from(select.selectedOptions || []).map(o => o.value).filter(Boolean)
  }

  getStateCodes(row) {
    const select = row.querySelector('select[name*="[state]"]')
    if (!select) return []
    if (select.tomselect) {
      const val = select.tomselect.getValue()
      return Array.isArray(val) ? val.filter(v => v !== 'all') : []
    }
    return Array.from(select.selectedOptions || []).map(o => o.value).filter(v => v && v !== 'all')
  }

  getDevicesRaw(row) {
    const select = row.querySelector('select[name*="[devices]"]')
    if (!select) return 'all'
    if (select.tomselect) return select.tomselect.getValue() || 'all'
    return select.value || 'all'
  }

  getFieldValue(container, selector) {
    const field = container.querySelector(selector)
    return field?.value?.trim() || ''
  }

  getSchedule(row) {
    const scheduleInput = row.querySelector('input[name*="[schedule]"]')
    if (!scheduleInput) return ''
    
    const scheduleText = scheduleInput.value?.trim() || ''
    return scheduleText
  }

  getLocation(row) {
    const locationContainer = row.querySelector('[data-controller*="location-selector"]')
    if (!locationContainer) return 'All Locations'

    const allLocationsButton = locationContainer.querySelector('[data-location-selector-target="selectorButton"]')
    const displayLocation = locationContainer.querySelector('[data-location-selector-target="displayLocation"]')
    
    if (allLocationsButton && !allLocationsButton.classList.contains('hidden') && 
        displayLocation && displayLocation.classList.contains('hidden')) {
      return 'All Locations'
    }

    const locationTextElement = locationContainer.querySelector('[data-location-selector-target="locationText"]')
    if (locationTextElement) {
      const parentDisplay = locationTextElement.closest('[data-location-selector-target="displayLocation"]')
      if (parentDisplay && !parentDisplay.classList.contains('hidden')) {
        const locationText = locationTextElement.textContent?.trim() || ''
        return locationText || 'All Locations'
      }
    }

    return 'All Locations'
  }

  getDevices(row) {
    const devicesSelect = row.querySelector('select[name*="[devices]"]')
    if (!devicesSelect) return ''

    const selectedOption = devicesSelect.options[devicesSelect.selectedIndex]
    if (selectedOption) {
      return selectedOption.textContent?.trim() || ''
    }

    const value = devicesSelect.value
    if (value === 'all') return 'All devices'
    if (value === 'IOS') return 'IOS'
    if (value === 'Android') return 'Android'
    
    return value || ''
  }

  getParentElement(selector) {
    return selector.closest('[data-controller="ab-rules"]');
  }

  hideElements(selector) {
    const parentElement = this.getParentElement(selector)
    if(parentElement) {
      parentElement.querySelector('#abRulesDescription').classList.remove('hidden');
      parentElement.querySelector('#abRulesSelectContainer').classList.add('hidden');
    }
  }

  showElements(selector) {
    const parentElement = this.getParentElement(selector)
    if(parentElement) {
      parentElement.querySelector('#abRulesDescription').classList.add('hidden');
      parentElement.querySelector('#abRulesSelectContainer').classList.remove('hidden');
    }
  }
}

